import { v1, toResult } from '@/infrastructure/api/v1/client';
import { storyPatchBody, type ChosenVisibility } from '@/infrastructure/api/v1/mappers/stories';
import { setStoryVisibility } from '@/modules/databaseService/scenes';
import { projectPatchBody } from '@/infrastructure/api/v1/mappers/projects';
import { splitDescription, joinDescription } from '@/modules/library/utils/storyAttributionUtils';
import { sanitizeLocationText } from '@/utils/locationUtils';

// Fields are optional: the modal only includes a field when its step was visible,
// so we never overwrite a value the user had no chance to edit.
export interface PublishFlowMetadata {
  sceneOriginId?: string | null;
  projectInstanceId?: string | null;
  userId?: string | null;
  storyName?: string;
  creatorName?: string;
  projectName?: string;
  projectDescription?: string;
  projectTags?: string[];
  sceneIntroduction?: string;
  address?: string;
  latitude?: string;
  longitude?: string;
  city?: string;
  visibility?: ChosenVisibility;
}

export const METADATA_READ_FAILED = "We couldn't read the story details, so it wasn't submitted. Please try again.";
export const METADATA_WRITE_FAILED = "We couldn't save the story details, so it wasn't submitted. Please try again.";

export async function writePublishFlowMetadata(meta: PublishFlowMetadata): Promise<void> {
  const hasLocation = meta.address !== undefined || meta.latitude !== undefined || meta.longitude !== undefined;
  let locationInfo: Record<string, unknown> | null = null;

  if (meta.sceneOriginId) {
    const sceneUpdate: Record<string, unknown> = {};
    if (meta.storyName !== undefined) sceneUpdate.scene_origin_name = meta.storyName;

    if (meta.sceneIntroduction !== undefined || hasLocation) {
      const { data: existing, error: readError } = await toResult(v1.GET('/api/v1/stories/{storyUniqueId}', {
        params: { path: { storyUniqueId: meta.sceneOriginId } },
      }));
      // The description keeps its credits block and the location its city, so neither is written from a failed read
      if (readError) throw new Error(METADATA_READ_FAILED, { cause: readError });

      if (meta.sceneIntroduction !== undefined) {
        const { attributions } = splitDescription(existing.description || '');
        const { userText } = splitDescription(meta.sceneIntroduction || '');
        sceneUpdate.scene_origin_description = joinDescription(userText, attributions);
      }

      if (hasLocation) {
        const lat = parseFloat(meta.latitude ?? '');
        const lng = parseFloat(meta.longitude ?? '');
        const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
        const existingCity = (existing.locationInfo as { city?: string } | null)?.city;
        const rawCity = meta.city !== undefined ? meta.city : existingCity;
        const city = sanitizeLocationText(rawCity) || undefined;
        const address = sanitizeLocationText(meta.address);
        locationInfo =
          hasCoords || address
            ? {
                ...(city ? { city } : {}),
                address: address || null,
                ...(hasCoords ? { coordinates: { lat, lng } } : {}),
              }
            : null;
        sceneUpdate.scene_origin_location_info = locationInfo;
      }
    }

    // Throws: a story must not go to review under a visibility the creator didn't pick
    if (meta.visibility) {
      const { error } = await setStoryVisibility(meta.sceneOriginId, meta.visibility);
      if (error) throw error;
    }

    if (Object.keys(sceneUpdate).length > 0) {
      const { error } = await toResult(v1.PATCH('/api/v1/stories/{storyUniqueId}', {
        params: { path: { storyUniqueId: meta.sceneOriginId } },
        body: storyPatchBody(sceneUpdate),
      }));
      if (error) throw new Error(METADATA_WRITE_FAILED, { cause: error });
    }
  }

  if (meta.projectInstanceId && meta.userId) {
    const projectUpdate: Record<string, unknown> = {};
    if (meta.creatorName !== undefined) projectUpdate.project_instance_source_name = meta.creatorName;
    if (meta.projectName !== undefined) projectUpdate.project_instance_display_name = meta.projectName;
    if (meta.projectDescription !== undefined) projectUpdate.project_instance_description = meta.projectDescription;
    if (meta.projectTags !== undefined) projectUpdate.project_instance_tags = meta.projectTags;
    if (hasLocation && meta.sceneOriginId && locationInfo) projectUpdate.project_instance_location_info = locationInfo;

    if (Object.keys(projectUpdate).length > 0) {
      const { data: project, error: readError } = await toResult(v1.GET('/api/v1/projects/{projectUniqueId}', {
        params: { path: { projectUniqueId: meta.projectInstanceId } },
      }));
      if (readError) throw new Error(METADATA_READ_FAILED, { cause: readError });
      // Owner only: the server also lets staff write it, which would overwrite the owner's project and creator name
      if (project.userUniqueId === meta.userId) {
        const { error } = await toResult(v1.PATCH('/api/v1/projects/{projectUniqueId}', {
          params: { path: { projectUniqueId: meta.projectInstanceId } },
          body: projectPatchBody(projectUpdate),
        }));
        if (error) throw new Error(METADATA_WRITE_FAILED, { cause: error });
      }
    }
  }
}
